/**
 * Face Groups panel — Blender-style named face-set management in edit mode.
 *
 * The face-domain analogue of `VertexGroupsPanel`: users create groups,
 * rename them inline, assign / remove the current face selection to/from
 * the active group, and select a group back into the viewport. The panel
 * is edit-mode-only and hides itself for curves and for non-face
 * granularities (face groups operate on face indices, so vertex/edge mode
 * has nothing to assign).
 */

import { Group as GroupIcon } from "lucide-react";
import { useStore } from "../../app/store.ts";
import type { FaceGroup } from "../../modeling/types.ts";
import { Button } from "../Button.tsx";
import { CollapsiblePanel, ListControl, Toggle } from "../shared.tsx";
import {
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_SMALL,
} from "../tokens.ts";

// ---------------------------------------------------------------------------
// Panel shell
// ---------------------------------------------------------------------------

export function FaceGroupsPanel({ bare }: { bare?: boolean } = {}) {
  const editMode = useStore((s) => s.editMode);
  const obj = useStore((s) =>
    editMode ? s.sceneObjects.get(editMode.objectId) : undefined,
  );

  const unavailable = !editMode || !obj || editMode.isCurve;
  const groups: FaceGroup[] = unavailable
    ? []
    : (obj.faceAttributes?.groups ?? []);
  const groupCount = groups.length;

  const content = unavailable ? (
    <div
      style={{
        fontSize: FONT_SIZE_SMALL,
        color: COLOR_TEXT_FAINT,
        padding: "4px 0",
      }}
    >
      Enter edit mode on a mesh to manage face groups.
    </div>
  ) : (
    <FaceGroupsBody
      objectId={editMode.objectId}
      groups={groups}
      activeGroupId={editMode.activeFaceGroupId ?? null}
      selectionSize={editMode.selectedFaces.size}
      showColors={editMode.showFaceGroupColors ?? true}
    />
  );

  if (bare) return content;

  return (
    <CollapsiblePanel
      id="faceGroups"
      title="Face Groups"
      icon={<GroupIcon size={11} />}
      summary={groupCount > 0 ? `${groupCount}` : undefined}
      defaultCollapsed={groupCount === 0}
    >
      {content}
    </CollapsiblePanel>
  );
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

function FaceGroupsBody({
  objectId,
  groups,
  activeGroupId,
  selectionSize,
  showColors,
}: {
  objectId: string;
  groups: FaceGroup[];
  activeGroupId: string | null;
  selectionSize: number;
  showColors: boolean;
}) {
  const createFaceGroup = useStore((s) => s.createFaceGroup);
  const deleteFaceGroup = useStore((s) => s.deleteFaceGroup);
  const renameFaceGroup = useStore((s) => s.renameFaceGroup);
  const setActiveFaceGroup = useStore((s) => s.setActiveFaceGroup);
  const assignSelectionToFaceGroup = useStore(
    (s) => s.assignSelectionToFaceGroup,
  );
  const removeSelectionFromFaceGroup = useStore(
    (s) => s.removeSelectionFromFaceGroup,
  );
  const selectFaceGroup = useStore((s) => s.selectFaceGroup);
  const setShowFaceGroupColors = useStore((s) => s.setShowFaceGroupColors);

  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null;
  const canAssign = activeGroup !== null && selectionSize > 0;
  const canRemove = canAssign;
  const canSelect = activeGroup !== null && activeGroup.faces.size > 0;
  const canDeselect = canSelect;

  const items = groups.map((g) => ({
    id: g.id,
    label: g.name,
    trailing: (
      <span
        style={{
          fontSize: FONT_SIZE_SMALL,
          color: COLOR_TEXT_SUBTLE,
          flexShrink: 0,
        }}
      >
        {g.faces.size}
      </span>
    ),
  }));

  return (
    <>
      <ListControl
        items={items}
        activeId={activeGroupId}
        onSelect={setActiveFaceGroup}
        onAdd={() => createFaceGroup(objectId)}
        onDelete={(id) => deleteFaceGroup(objectId, id)}
        onRename={(id, name) => renameFaceGroup(objectId, id, name)}
        emptyMessage="No face groups"
        maxHeight={160}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 4,
          marginTop: 8,
        }}
      >
        <ActionButton
          label="Assign"
          title="Add the current face selection to the active group"
          enabled={canAssign}
          onClick={() =>
            activeGroup && assignSelectionToFaceGroup(objectId, activeGroup.id)
          }
        />
        <ActionButton
          label="Remove"
          title="Remove the current face selection from the active group"
          enabled={canRemove}
          onClick={() =>
            activeGroup &&
            removeSelectionFromFaceGroup(objectId, activeGroup.id)
          }
        />
        <ActionButton
          label="Select"
          title="Select the active group's faces in the viewport"
          enabled={canSelect}
          onClick={() =>
            activeGroup && selectFaceGroup(objectId, activeGroup.id, "add")
          }
        />
        <ActionButton
          label="Deselect"
          title="Remove the active group's faces from the selection"
          enabled={canDeselect}
          onClick={() =>
            activeGroup && selectFaceGroup(objectId, activeGroup.id, "subtract")
          }
        />
      </div>

      <Toggle
        label="Show Surface Colors"
        checked={showColors}
        onChange={setShowFaceGroupColors}
      />
    </>
  );
}

function ActionButton({
  label,
  title,
  enabled,
  onClick,
}: {
  label: string;
  title: string;
  enabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      title={title}
      variant="secondary"
      size="xs"
      fullWidth
    >
      {label}
    </Button>
  );
}
